import {t} from '@lingui/macro';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput, StyledBoxDiv} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {useLocalize} from '../../../srcCommon/internationalization/internationalizationContext';
import {
  DataFieldsTableColumnModel,
  DataFieldsTableColumnTypesEnum,
} from '../../models/api/dataFieldsTableModel';

/*
 * Props.
 */

export interface DataFieldsTableCellProps {
  column: DataFieldsTableColumnModel;
  data: string | undefined;
  onChange: HandlerOf<string | undefined>;
}

/*
 * Styles.
 */

const StyledBoxedInput = styled(BoxedInput)`
  margin: 7px;
  width: 100%;

  ${StyledBoxDiv} {
    background-color: transparent;

    :hover:not(:focus-within) {
      background-color: ${(p) => p.theme.greys.shade20};
    }
  }
`;

/*
 * Component.
 */

export const DataFieldsTableCell: FC<DataFieldsTableCellProps> = ({...props}) => {
  switch (props.column.type) {
    case DataFieldsTableColumnTypesEnum.STRING:
      return <DataTableTextCell {...props} />;
    default:
      return <>Missing renderer: ${props.column.type}</>;
  }
};

const DataTableTextCell: FC<DataFieldsTableCellProps> = ({data: initialValue, onChange}) => {
  const localize = useLocalize();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = useState(initialValue);

  // If the initialValue is changed externally, sync it up with our state
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const onBlur = () => {
    if (value !== initialValue) {
      onChange(value);
    }
  };

  return (
    <StyledBoxedInput noMargin size={VisualSizesEnum.SMALL}>
      {(childProps) => (
        <BoxedInputElement
          {...childProps}
          placeholder={localize(t('rule-editor-data-table-text-cell-placeholder')`Enter data`)}
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (event.key === Key.Enter && !event.shiftKey) {
              onBlur();
            }
          }}
          type={BoxedInputElementTypesEnum.TEXT}
          value={value}
          onChange={(v) => setValue(v)}
        />
      )}
    </StyledBoxedInput>
  );
};
